/* ============================================================
   section-review-carousel.js
   assets/section-review-carousel.js

   Features:
   1. Clone cards for seamless infinity loop
   2. CSS animation auto-scroll (direction from schema)
   3. Hover → pause
   4. Arrows → manual nav (resumes auto after 3s)
   5. Dots → jump to index
   6. Mouse drag + touch swipe (axis-locked, live feedback)
   ============================================================ */

(function () {
  'use strict';

  function initCarousel(section) {
    var track     = section.querySelector('[data-rc-track]');
    var trackWrap = section.querySelector('[data-rc-track-wrap]');
    var dotsWrap  = section.querySelector('[data-rc-dots]');
    var prevBtn   = section.querySelector('[data-rc-prev]');
    var nextBtn   = section.querySelector('[data-rc-next]');

    if (!track) return;

    var direction    = section.dataset.direction || 'ltr';
    var cards        = Array.from(track.querySelectorAll('[data-rc-card]'));
    var totalCards   = cards.length;
    var currentIndex = 0;
    var resumeTimer  = null;
    var isAuto       = true;

    if (totalCards === 0) return;

    /* ── 1. Clone cards for infinity loop ──────────────────── */
    cards.forEach(function (card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('rc-clone');
      track.appendChild(clone);
    });

    /* ── 2. Auto scroll ────────────────────────────────────── */
    function startAutoScroll() {
      track.style.transform  = '';
      track.style.transition = '';
      track.style.animation  = '';
      track.classList.remove('is-paused');
      track.classList.add('is-auto-scrolling');
      if (direction === 'rtl') track.classList.add('rc-rtl');
      isAuto = true;
    }

    function pauseAutoScroll() {
      track.classList.add('is-paused');
    }

    function stopAutoScroll() {
      track.classList.remove('is-auto-scrolling', 'is-paused', 'rc-rtl');
      track.style.animation = 'none';
      isAuto = false;
    }

    /* ── 3. Hover pause ────────────────────────────────────── */
    trackWrap.addEventListener('mouseenter', function () {
      if (isAuto) pauseAutoScroll();
    });

    trackWrap.addEventListener('mouseleave', function () {
      if (isAuto) track.classList.remove('is-paused');
    });

    /* ── 4. Manual navigation ──────────────────────────────── */
    function getCardWidth() {
      var card = track.querySelector('[data-rc-card]');
      if (!card) return 0;
      var gap = parseInt(window.getComputedStyle(track).gap) || 24;
      return card.offsetWidth + gap;
    }

    function goTo(index, animate) {
      if (index < 0) index = totalCards - 1;
      if (index >= totalCards) index = 0;
      currentIndex = index;

      track.style.transition = animate !== false
        ? 'transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94)'
        : 'none';

      track.style.transform = 'translateX(-' + (currentIndex * getCardWidth()) + 'px)';
      updateDots();
    }

    function scheduleResume() {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        startAutoScroll();
      }, 3000);
    }

    function manualNav(delta) {
      stopAutoScroll();
      goTo(currentIndex + delta, true);
      scheduleResume();
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { manualNav(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { manualNav(1); });

    /* ── 5. Dots ────────────────────────────────────────────── */
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (var i = 0; i < totalCards; i++) {
        (function (idx) {
          var dot = document.createElement('button');
          dot.className = 'rc-dot' + (idx === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', 'Go to review ' + (idx + 1));
          dot.addEventListener('click', function () {
            stopAutoScroll();
            goTo(idx, true);
            scheduleResume();
          });
          dotsWrap.appendChild(dot);
        })(i);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      dotsWrap.querySelectorAll('.rc-dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === currentIndex);
      });
    }

    buildDots();

    /* ── 6. Mouse drag + touch swipe ───────────────────────── */
    var dragStartX    = null;
    var dragStartY    = null;
    var isDragging    = false;
    var dragBaseOffset = 0;

    function getComputedOffset() {
      var matrix = window.getComputedStyle(track).transform;
      if (matrix && matrix !== 'none') {
        var match = matrix.match(/matrix.*\((.+)\)/);
        if (match) {
          return parseFloat(match[1].split(', ')[4]) || 0;
        }
      }
      return -(currentIndex * getCardWidth());
    }

    function onDragStart(x, y) {
      dragStartX = x;
      dragStartY = y;
      isDragging = false;
      dragBaseOffset = getComputedOffset();
      stopAutoScroll();
      track.style.transition = 'none';
      track.style.transform  = 'translateX(' + dragBaseOffset + 'px)';
    }

    function onDragMove(x, y) {
      if (dragStartX === null) return;
      var dx = x - dragStartX;
      var dy = y - dragStartY;

      if (!isDragging) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          /* vertical scroll — cancel drag */
          dragStartX = null;
          dragStartY = null;
          startAutoScroll();
          return;
        }
        isDragging = true;
      }
      track.style.transform = 'translateX(' + (dragBaseOffset + dx) + 'px)';
    }

    function onDragEnd(x) {
      if (dragStartX === null) return;
      var diff = dragStartX - x;

      if (isDragging && Math.abs(diff) > 40) {
        goTo(currentIndex + (diff > 0 ? 1 : -1), true);
      } else {
        /* snap back */
        track.style.transition = 'transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94)';
        track.style.transform  = 'translateX(-' + (currentIndex * getCardWidth()) + 'px)';
      }

      scheduleResume();
      dragStartX = null;
      dragStartY = null;
      isDragging = false;
    }

    /* Mouse */
    trackWrap.addEventListener('mousedown', function (e) {
      e.preventDefault();
      onDragStart(e.clientX, e.clientY);
    });

    window.addEventListener('mousemove', function (e) {
      if (dragStartX !== null) onDragMove(e.clientX, e.clientY);
    });

    window.addEventListener('mouseup', function (e) {
      if (dragStartX !== null) onDragEnd(e.clientX);
    });

    /* Touch */
    trackWrap.addEventListener('touchstart', function (e) {
      onDragStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });

    trackWrap.addEventListener('touchmove', function (e) {
      if (dragStartX === null) return;
      if (isDragging) e.preventDefault();
      onDragMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: false });

    trackWrap.addEventListener('touchend', function (e) {
      if (dragStartX !== null) onDragEnd(e.changedTouches[0].clientX);
    });

    /* ── Touchpad two-finger horizontal swipe (wheel deltaX) ── */
    trackWrap.addEventListener('wheel', function (e) {
      /* Only trigger when horizontal scroll dominates */
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();

      stopAutoScroll();

      /* Live translate during swipe */
      var currentOffset = getComputedOffset();
      var newOffset = currentOffset - e.deltaX;
      track.style.transition = 'none';
      track.style.animation  = 'none';
      track.style.transform  = 'translateX(' + newOffset + 'px)';

      /* Snap to nearest card after swipe ends */
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        var cardW   = getCardWidth();
        var snapped = Math.round(-newOffset / cardW);
        var max     = totalCards - 1;
        if (snapped < 0)   snapped = 0;
        if (snapped > max) snapped = max;
        goTo(snapped, true);
        scheduleResume();
      }, 80);
    }, { passive: false });

    /* ── Start ─────────────────────────────────────────────── */
    startAutoScroll();
  }

  /* ── Boot ──────────────────────────────────────────────────*/
  function init() {
    document.querySelectorAll('[data-rc-section]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify live editor reload */
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-rc-section]');
    if (s) initCarousel(s);
  });

})();